(() => {
  const logic = {
    valid(value, code, days, now = Date.now()) {
      return Boolean(value && value.code === code && Number.isFinite(value.registeredAt) && value.registeredAt <= now && now - value.registeredAt < days * 86400000);
    },
    closed(state, reason) {
      return state ? {...state, closedReason: reason, fallback: reason === 'auto' && !state.copied} : null;
    },
    copied(state) {
      return state ? {...state, copied: true, fallback: false} : null;
    },
    showCoupon(state, enabled, eligible) {
      return Boolean(enabled && eligible && state);
    }
  };
  window.CareizRewardLogic = Object.freeze(logic);
  if (!customElements.get('careiz-coupon-slot')) {
    customElements.define('careiz-coupon-slot', class extends HTMLElement {
      connectedCallback() { document.querySelector('careiz-reward')?.renderSlots?.(); }
    });
  }
  if (customElements.get('careiz-reward')) return;
  customElements.define('careiz-reward', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.dialog = this.querySelector('dialog');
      this.code = this.dataset.code.trim();
      this.storageKey = 'careiz-reward-v2:' + this.dataset.scope;
      this.days = Number(this.dataset.days);
      this.allowed = Boolean(document.querySelector('careiz-signup'));
      this.eligible = new Set(this.dataset.products.split(','));
      this.state = this.readState();
      const query = new URLSearchParams(location.search);
      this.preview = this.dataset.draft === 'true' && (query.get('careiz_reward_preview') === '1' || query.get('careiz_coupon_preview') === '1');
      this.demo = this.preview || this.dataset.editorPreview === 'true';
      if (this.demo) this.state = {code: this.code, registeredAt: Date.now(), copied: false, fallback: query.get('careiz_coupon_preview') === '1'};
      this.querySelectorAll('[data-reward-close]').forEach(button => button.addEventListener('click', () => this.close('manual'), {signal: this.signal}));
      this.querySelector('[data-keep-open]').addEventListener('click', () => this.stopTimer(), {signal: this.signal});
      this.querySelector('[data-reward-copy]').addEventListener('click', event => this.copy(event.currentTarget), {signal: this.signal});
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close('manual'); }, {signal: this.signal});
      this.dialog.addEventListener('close', () => this.restoreFocus(), {signal: this.signal});
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close('manual');
      }, {signal: this.signal});
      window.addEventListener('storage', event => {
        if (event.key !== this.storageKey) return;
        this.state = this.readState();
        this.renderSlots();
      }, {signal: this.signal});
      document.addEventListener('shopify:section:select', event => {
        if (event.detail.sectionId !== this.dataset.section || this.dataset.editor !== 'true') return;
        this.demo = true;
        this.state = {code: this.code, registeredAt: Date.now(), copied: false, fallback: false};
        document.querySelector('careiz-signup dialog[open]')?.close();
        this.open();
      }, {signal: this.signal});
      document.addEventListener('shopify:section:deselect', event => {
        if (event.detail.sectionId === this.dataset.section && this.dialog.open) this.close('manual');
      }, {signal: this.signal});
      document.addEventListener('careiz:reward-page-copy', event => {
        if (event.detail.code === this.code) {
          this.state = logic.copied(this.state);
          this.writeState();
          this.renderSlots();
        }
      }, {signal: this.signal});
      this.renderSlots();
      if (this.demo && !this.state.fallback) this.open();
      this.dispatchEvent(new CustomEvent('careiz:reward-ready', {bubbles: true}));
    }
    readState() {
      let value;
      for (const name of ['localStorage', 'sessionStorage']) {
        try { value = JSON.parse(window[name].getItem(this.storageKey)); } catch {}
        if (logic.valid(value, this.code, this.days)) return value;
      }
      return null;
    }
    writeState() {
      if (this.demo) return;
      for (const name of ['sessionStorage', 'localStorage']) {
        try { if (this.state) window[name].setItem(this.storageKey, JSON.stringify(this.state)); } catch {}
      }
    }
    registered() {
      if (!this.allowed) return;
      const previous = this.readState();
      this.state = {code: this.code, registeredAt: Date.now(), copied: previous?.copied === true, fallback: false};
      this.writeState();
      this.renderSlots();
      this.open();
    }
    open() {
      if (!this.allowed || this.dialog.open) return;
      document.querySelector('careiz-signup dialog[open]')?.close();
      this.previousFocus = document.activeElement;
      this.previousOverflow = document.documentElement.style.overflow;
      this.querySelector('[data-reward-status]').textContent = '';
      this.querySelector('[data-reward-copy]').textContent = this.dataset.copyLabel;
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      this.startTimer();
    }
    startTimer() {
      this.stopTimer();
      if (this.dataset.autoClose !== 'true') return;
      this.remaining = Number(this.dataset.seconds);
      this.querySelector('[data-reward-timer]').hidden = false;
      this.querySelector('[data-keep-open]').hidden = false;
      this.updateTimerLabel();
      this.interval = setInterval(() => {
        if (document.hidden || this.copyPending || !this.dialog.open) return;
        this.remaining -= 1;
        this.updateTimerLabel();
        if (this.remaining <= 0) this.close('auto');
      }, 1000);
    }
    updateTimerLabel() {
      this.querySelector('[data-reward-countdown]').textContent = this.dataset.countdown.replace('{seconds}', String(this.remaining));
    }
    stopTimer() {
      clearInterval(this.interval);
      this.querySelector('[data-reward-timer]').hidden = true;
      this.querySelector('[data-keep-open]').hidden = true;
    }
    close(reason) {
      if (!this.dialog.open) return;
      this.stopTimer();
      if (!this.demo) this.state = this.readState() || this.state;
      this.state = logic.closed(this.state, reason);
      this.writeState();
      this.dialog.close();
      this.renderSlots();
    }
    restoreFocus() {
      document.documentElement.style.overflow = this.previousOverflow || '';
      if (this.previousFocus?.isConnected && !this.previousFocus.closest('dialog:not([open])')) this.previousFocus.focus();
    }
    async copy(button) {
      const region = button.closest('[data-copy-region]');
      const status = region.querySelector('[data-reward-status]');
      this.copyPending = true;
      button.disabled = true;
      try {
        await navigator.clipboard.writeText(this.code);
        if (!this.demo) this.state = this.readState() || this.state;
        this.state = logic.copied(this.state);
        this.writeState();
        button.textContent = this.dataset.copiedLabel;
        status.textContent = this.dataset.copiedLabel;
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => this.renderSlots(), 1600);
      } catch {
        this.stopTimer();
        status.textContent = this.dataset.copyFallback;
        const code = region.querySelector('[data-code-text]');
        const range = document.createRange();
        range.selectNodeContents(code);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      } finally {
        this.copyPending = false;
        button.disabled = false;
      }
    }
    renderSlots() {
      if (!this.allowed) return;
      const eligibleState = logic.valid(this.state, this.code, this.days) ? this.state : null;
      document.querySelectorAll('careiz-coupon-slot').forEach(slot => {
        const show = logic.showCoupon(eligibleState, this.dataset.inlineEnabled === 'true', this.eligible.has(slot.dataset.productId));
        slot.hidden = !show;
        if (!show) { slot.replaceChildren(); return; }
        if (!slot.querySelector('[data-copy-region]')) {
          slot.append(this.querySelector('template[data-inline-coupon]').content.cloneNode(true));
          slot.querySelector('[data-reward-copy]').addEventListener('click', event => this.copy(event.currentTarget));
        }
      });
    }
    disconnectedCallback() {
      this.stopTimer();
      clearTimeout(this.copyTimer);
      this.controller?.abort();
      if (this.dialog?.open) { this.dialog.close(); this.restoreFocus(); }
      this.initialized = false;
    }
  });
})();

